'use client';

import type { Editor } from '@tiptap/react';
import { Layers3, LayoutGrid } from 'lucide-react';
import type { ComponentType } from 'react';
import { useCallback } from 'react';

// --- Icons ---
import { BlockMathIcon } from '@/components/tiptap-editor/tiptap-icons/block-math-icon';
import { BlockquoteIcon } from '@/components/tiptap-editor/tiptap-icons/blockquote-icon';
import { DetailsIcon } from '@/components/tiptap-editor/tiptap-icons/details-icon';
import { HeadingOneIcon } from '@/components/tiptap-editor/tiptap-icons/heading-one-icon';
import { HeadingThreeIcon } from '@/components/tiptap-editor/tiptap-icons/heading-three-icon';
import { HeadingTwoIcon } from '@/components/tiptap-editor/tiptap-icons/heading-two-icon';
import { IframeIcon } from '@/components/tiptap-editor/tiptap-icons/iframe-icon';
import { InlineMathIcon } from '@/components/tiptap-editor/tiptap-icons/inline-math-icon';
import { InsertImageIcon } from '@/components/tiptap-editor/tiptap-icons/insert-image-icon';
import { ListIcon } from '@/components/tiptap-editor/tiptap-icons/list-icon';
import { ListOrderedIcon } from '@/components/tiptap-editor/tiptap-icons/list-ordered-icon';
import { SymbolsIcon } from '@/components/tiptap-editor/tiptap-icons/symbols-icon';
import { TableIcon } from '@/components/tiptap-editor/tiptap-icons/table-icon';
import { TypeIcon } from '@/components/tiptap-editor/tiptap-icons/type-icon';
import { UploadImageIcon } from '@/components/tiptap-editor/tiptap-icons/upload-image-icon';
import { ZyGdocDrawingIcon } from '@/components/tiptap-editor/tiptap-icons/zy-gdoc-drawing-icon';
import { ZyAsideIcon } from '@/components/tiptap-editor/tiptap-icons/zyaside-icon';
import { ZyCodeIcon } from '@/components/tiptap-editor/tiptap-icons/zycode-icon';
import { ZyConsoleIcon } from '@/components/tiptap-editor/tiptap-icons/zyconsole-icon';
import { ZyContentIcon } from '@/components/tiptap-editor/tiptap-icons/zycontent-icon';
import { ZyDEIcon } from '@/components/tiptap-editor/tiptap-icons/zyde-icon';
import { ZyDefnIcon } from '@/components/tiptap-editor/tiptap-icons/zydefn-icon';
import { ZyExerciseIcon } from '@/components/tiptap-editor/tiptap-icons/zyexercise-icon';
import { ZyExploreIcon } from '@/components/tiptap-editor/tiptap-icons/zyexplore-icon';
import { ZyFootnoteIcon } from '@/components/tiptap-editor/tiptap-icons/zyfootnote-icon';
import { ZyInlineEnumIcon } from '@/components/tiptap-editor/tiptap-icons/zyinlineenum-icon';
import { ZyProofIcon } from '@/components/tiptap-editor/tiptap-icons/zyproof-icon';
import { ZyStudioIcon } from '@/components/tiptap-editor/tiptap-icons/zystudio-icon';
import { ZyYoutubeVideoIcon } from '@/components/tiptap-editor/tiptap-icons/zyyoutubevideo-icon';

// --- Lib ---
import { canSeeImageLibrary } from '@/lib/image-from-library';
import { isNodeInSchema } from '@/lib/tiptap-utils';
import { getEditorAllowedZyVersions, isSelectionTopNode } from '@/lib/zy-tiptap-utils';

// --- Tiptap UI ---
import type { TableInsertType } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-trigger-button/use-table-trigger';
import {
    canInsertGridLayout,
    canInsertTable,
} from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-trigger-button/use-table-trigger';
import type { SuggestionItem } from '@/components/tiptap-editor/tiptap-ui-utils/suggestion-menu';
import { detailsContent } from '@/components/tiptap-editor/tiptap-ui/details-button/details-button';
import { mathContent } from '@/components/tiptap-editor/tiptap-ui/math-button/math-button';
import { activityOptions, isActivityButtonDisabled } from '@/components/tiptap-editor/tiptap-ui/question-set-menu/question-set-menu';
import { canInsertZyExample, zyExampleOptions } from '@/components/tiptap-editor/tiptap-ui/zyexample-menu/zyexample-menu';
import { zyFootnoteContent } from '@/components/tiptap-editor/tiptap-ui/zyfootnote-button/zyfootnote-button';
import { isInlineEnumDisabled } from '@/components/tiptap-editor/tiptap-ui/zyinlineenum-button/zyinlineenum-button';
import { zyToolOptions } from '@/components/tiptap-editor/tiptap-ui/zytools-dropdown-menu/zytools-dropdown-menu';

export interface SlashMenuConfig {
    enabledItems?: SlashMenuItemType[];
    customItems?: SuggestionItem[];
    onSymbolsTrigger?: (editor: Editor) => void;
    onImageLibraryTrigger?: (editor: Editor) => void;
    onTableInsertTrigger?: (editor: Editor, type: TableInsertType) => void;
    itemGroups?: {
        [key in SlashMenuItemType]?: string;
    };
    showGroups?: boolean;
}

type SlashMenuText = {
    title: string;
    subtext: string;
    keywords: string[];
    badge: SuggestionItem['badge'];
    group: string;
};

type SlashMenuItemImpl = {
    check: (editor: Editor) => boolean;
    action: (props: { editor: Editor }) => void;
};

function zyContentText(title: string, badge: ComponentType<{ className?: string }>, keywords: string[] = [title]): SlashMenuText {
    return {
        title,
        subtext: `Insert ${title}`,
        keywords,
        // Option menus type icons as ComponentType; SuggestionItem only allows FC/Memo.
        badge: badge as SuggestionItem['badge'],
        group: 'zyContent',
    };
}

const zyExampleTexts = Object.fromEntries(
    zyExampleOptions.map(option => [`zyexample_${option.type}`, zyContentText(option.label, option.icon)]),
);

const activityTexts = Object.fromEntries(
    activityOptions.map(option => [`activity_${option.type}`, zyContentText(option.label, option.icon)]),
);

const zyToolTexts = Object.fromEntries(
    zyToolOptions.map(option => [option.extensionName, zyContentText(option.label, option.icon, [option.label, option.extensionName])]),
);

const texts = {
    // Style
    text: {
        title: 'Text',
        subtext: 'Regular text paragraph',
        keywords: ['p', 'paragraph', 'text'],
        badge: TypeIcon,
        group: 'Style',
    },
    heading_1: {
        title: 'Heading 1',
        subtext: 'Top-level heading',
        keywords: ['h', 'heading1', 'h1'],
        badge: HeadingOneIcon,
        group: 'Style',
    },
    heading_2: {
        title: 'Heading 2',
        subtext: 'Key section heading',
        keywords: ['h2', 'heading2', 'subheading'],
        badge: HeadingTwoIcon,
        group: 'Style',
    },
    heading_3: {
        title: 'Heading 3',
        subtext: 'Subsection and group heading',
        keywords: ['h3', 'heading3', 'subheading'],
        badge: HeadingThreeIcon,
        group: 'Style',
    },
    bullet_list: {
        title: 'Bullet List',
        subtext: 'List with unordered items',
        keywords: ['ul', 'li', 'list', 'bulletlist', 'bullet list'],
        badge: ListIcon,
        group: 'Style',
    },
    ordered_list: {
        title: 'Numbered List',
        subtext: 'List with ordered items',
        keywords: ['ol', 'li', 'list', 'numberedlist', 'numbered list'],
        badge: ListOrderedIcon,
        group: 'Style',
    },
    quote: {
        title: 'Blockquote',
        subtext: 'Blockquote block',
        keywords: ['quote', 'blockquote'],
        badge: BlockquoteIcon,
        group: 'Style',
    },

    table: {
        title: 'Table',
        subtext: 'Insert a table',
        keywords: ['table', 'zytable', 'rows', 'columns'],
        badge: TableIcon,
        group: 'Tables',
    },
    layout_grid: {
        title: 'Grid layout',
        subtext: 'Insert a borderless layout grid',
        keywords: ['grid layout', 'layout', 'grid', 'columns', 'rows'],
        badge: LayoutGrid,
        group: 'Tables',
    },

    // Symbols
    symbols: {
        title: 'Symbols',
        subtext: 'Insert a symbol',
        keywords: ['symbol', 'symbols'],
        badge: SymbolsIcon,
        group: 'Symbols',
    },

    // Math
    block_math: {
        title: 'Block Math',
        subtext: 'Insert block math',
        keywords: ['block math', 'math'],
        badge: BlockMathIcon,
        group: 'Math',
    },
    inline_math: {
        title: 'Inline Math',
        subtext: 'Insert inline math',
        keywords: ['inline math', 'math'],
        badge: InlineMathIcon,
        group: 'Math',
    },

    // zyContent
    zydefn: {
        title: 'zyDefinition',
        subtext: 'Insert zyDefinition',
        keywords: ['zyDefinition', 'zy definition', 'zydefn'],
        badge: ZyDefnIcon,
        group: 'zyContent',
    },
    zyproof: {
        title: 'zyProof',
        subtext: 'Insert zyProof',
        keywords: ['proof', 'zyproof'],
        badge: ZyProofIcon,
        group: 'zyContent',
    },
    zyversion: {
        title: 'zyVersion',
        subtext: 'Insert zyVersion',
        keywords: ['version', 'zyversion', 'zy version'],
        badge: Layers3,
        group: 'zyContent',
    },
    details: zyContentText('Details', DetailsIcon),
    zyfootnote: zyContentText('zyFootnote', ZyFootnoteIcon, ['zyFootnote', 'footnote']),
    zyinlineenum: zyContentText('zyInlineEnum', ZyInlineEnumIcon, ['zyInlineEnum', 'inline enum']),
    zydatamember: zyContentText('zyDataMember', ZyContentIcon, ['zyDataMember', 'data member']),
    zyfunction: zyContentText('zyFunction', ZyContentIcon, ['zyFunction', 'function']),
    zyfctname: zyContentText('zyFctName', ZyContentIcon, ['zyFctName', 'function name']),
    zycode: zyContentText('zyCode', ZyCodeIcon, ['zyCode', 'code']),
    zyconsole: zyContentText('zyConsole', ZyConsoleIcon, ['zyConsole', 'console']),
    zyde: zyContentText('zyDE', ZyDEIcon, ['zyDE', 'de']),
    ...zyExampleTexts,
    ...activityTexts,
    zyaside: zyContentText('zyAside', ZyAsideIcon, ['zyAside', 'aside']),
    zyexplore: zyContentText('zyExplore', ZyExploreIcon, ['zyExplore', 'explore']),
    zyexercise: zyContentText('zyExercise', ZyExerciseIcon, ['zyExercise', 'exercise']),
    zyyoutubevideo: zyContentText('zyYoutubeVideo', ZyYoutubeVideoIcon, ['zyYoutubeVideo', 'youtube', 'video']),
    iframe: zyContentText('iframe', IframeIcon),
    zystudio: zyContentText('zyStudio', ZyStudioIcon, ['zyStudio', 'studio']),
    ...zyToolTexts,

    // Image
    image_from_library: {
        title: 'Insert from library',
        subtext: "Choose an image from this book's library",
        keywords: ['image', 'library', 'gallery', 'img', 'picture', 'media'],
        badge: InsertImageIcon,
        group: 'Image',
    },
    image_upload: {
        title: 'Image Upload',
        subtext: 'Upload an image',
        keywords: ['image', 'imageUpload', 'upload', 'img', 'picture', 'media', 'url'],
        badge: UploadImageIcon,
        group: 'Image',
    },
    zy_gdoc_drawing: {
        title: 'zyGdocDrawing',
        subtext: 'Insert zyGdocDrawing',
        keywords: ['zyGdocDrawing', 'zy gdoc drawing', 'zygdocdrawing'],
        badge: ZyGdocDrawingIcon,
        group: 'Image',
    },
};

export type SlashMenuItemType = keyof typeof texts;

const getItemImplementations = (config?: SlashMenuConfig): Record<SlashMenuItemType, SlashMenuItemImpl> => {
    const zyExampleImplementations = Object.fromEntries(
        zyExampleOptions.map(option => [
            `zyexample_${option.type}`,
            {
                check: (editor: Editor) => canInsertZyExample(editor) && isNodeInSchema('zyExample', editor),
                action: ({ editor }: { editor: Editor }) => {
                    if (option.opensTableInsert) {
                        config?.onTableInsertTrigger?.(editor, 'zytable');
                        return;
                    }
                    option.command?.(editor);
                },
            },
        ]),
    );

    const activityImplementations = Object.fromEntries(
        activityOptions.map(option => [
            `activity_${option.type}`,
            {
                check: (editor: Editor) => !isActivityButtonDisabled(editor, option.type) && isNodeInSchema(option.type, editor),
                action: ({ editor }: { editor: Editor }) => option.command(editor),
            },
        ]),
    );

    const zyToolImplementations = Object.fromEntries(
        zyToolOptions.map(option => [
            option.extensionName,
            {
                check: (editor: Editor) => isSelectionTopNode(editor) && isNodeInSchema(option.extensionName, editor),
                action: ({ editor }: { editor: Editor }) => option.command(editor),
            },
        ]),
    );

    return {
        // Style
        text: {
            check: (editor: Editor) => isNodeInSchema('paragraph', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().setParagraph().run();
            },
        },
        heading_1: {
            check: (editor: Editor) => isNodeInSchema('heading', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().toggleHeading({ level: 1 }).run();
            },
        },
        heading_2: {
            check: (editor: Editor) => isNodeInSchema('heading', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().toggleHeading({ level: 2 }).run();
            },
        },
        heading_3: {
            check: (editor: Editor) => isNodeInSchema('heading', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().toggleHeading({ level: 3 }).run();
            },
        },
        bullet_list: {
            check: (editor: Editor) => isNodeInSchema('bulletList', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().toggleBulletList().run();
            },
        },
        ordered_list: {
            check: (editor: Editor) => isNodeInSchema('orderedList', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().toggleOrderedList().run();
            },
        },
        quote: {
            check: (editor: Editor) => isNodeInSchema('blockquote', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().toggleBlockquote().run();
            },
        },
        table: {
            check: (editor: Editor) => isNodeInSchema('table', editor) && canInsertTable(editor),
            action: ({ editor }: { editor: Editor }) => {
                config?.onTableInsertTrigger?.(editor, 'table');
            },
        },
        layout_grid: {
            check: (editor: Editor) => isNodeInSchema('table', editor) && canInsertGridLayout(editor),
            action: ({ editor }: { editor: Editor }) => {
                config?.onTableInsertTrigger?.(editor, 'grid');
            },
        },

        // Symbols
        symbols: {
            check: () => true,
            action: ({ editor }: { editor: Editor }) => {
                if (config?.onSymbolsTrigger) {
                    config.onSymbolsTrigger(editor);
                    return;
                }
            },
        },

        // Math
        block_math: {
            check: () => true,
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().insertBlockMath(mathContent).run();
            },
        },
        inline_math: {
            check: () => true,
            action: ({ editor }: { editor: Editor }) => {
                editor.chain().focus().insertInlineMath(mathContent).run();
            },
        },

        // zyContent
        zydefn: {
            check: () => true,
            action: ({ editor }: { editor: Editor }) => {
                if (editor.commands.removeZyDefn()) {
                    return;
                }
                editor
                    .chain()
                    .focus()
                    .insertDefinitionWithContent({ content: [{ type: 'text', text: '\u200B' }] })
                    .run();
            },
        },
        zyproof: {
            check: () => true,
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyProof();
            },
        },
        zyversion: {
            check: (editor: Editor) =>
                isNodeInSchema('zyVersionBlock', editor) &&
                isNodeInSchema('zyVersionInline', editor) &&
                getEditorAllowedZyVersions(editor).length > 0,
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.toggleOrInsertZyVersion();
            },
        },
        details: {
            check: (editor: Editor) => isNodeInSchema('details', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertContent(detailsContent);
            },
        },
        zyfootnote: {
            check: (editor: Editor) => isNodeInSchema('zyFootnote', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyFootnote(zyFootnoteContent);
            },
        },
        zyinlineenum: {
            check: (editor: Editor) => !isInlineEnumDisabled(editor) && isNodeInSchema('zyInlineEnum', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyInlineEnum();
            },
        },
        zydatamember: {
            check: (editor: Editor) => isNodeInSchema('zyVersionInline', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyDataMember();
            },
        },
        zyfunction: {
            check: (editor: Editor) => isNodeInSchema('zyVersionInline', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyFunction();
            },
        },
        zyfctname: {
            check: (editor: Editor) => isNodeInSchema('zyVersionInline', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyFctName();
            },
        },
        zycode: {
            check: (editor: Editor) => isNodeInSchema('zyCode', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyCode();
            },
        },
        zyconsole: {
            check: (editor: Editor) => isNodeInSchema('zyConsole', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyConsole();
            },
        },
        zyde: {
            check: (editor: Editor) => isSelectionTopNode(editor) && isNodeInSchema('zyDE', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyDE();
            },
        },
        ...zyExampleImplementations,
        ...activityImplementations,
        zyaside: {
            check: (editor: Editor) => isSelectionTopNode(editor) && isNodeInSchema('zyAside', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyAside();
            },
        },
        zyexplore: {
            check: (editor: Editor) => isSelectionTopNode(editor) && isNodeInSchema('zyExplore', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyExplore();
            },
        },
        zyexercise: {
            check: (editor: Editor) => isSelectionTopNode(editor) && isNodeInSchema('zyExercise', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyExercise();
            },
        },
        zyyoutubevideo: {
            check: (editor: Editor) => isSelectionTopNode(editor) && isNodeInSchema('zyYoutubeVideo', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyYoutubeVideo();
            },
        },
        iframe: {
            check: (editor: Editor) => isSelectionTopNode(editor) && isNodeInSchema('iframe', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertIframe();
            },
        },
        zystudio: {
            check: (editor: Editor) => isSelectionTopNode(editor) && isNodeInSchema('zyStudio', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyStudio();
            },
        },
        ...zyToolImplementations,

        // Image
        image_from_library: {
            check: (editor: Editor) => canSeeImageLibrary(editor),
            action: ({ editor }: { editor: Editor }) => {
                config?.onImageLibraryTrigger?.(editor);
            },
        },
        image_upload: {
            check: (editor: Editor) => isNodeInSchema('imageUpload', editor),
            action: ({ editor }: { editor: Editor }) => {
                editor
                    .chain()
                    .focus()
                    .insertContent({
                        type: 'imageUpload',
                    })
                    .run();
            },
        },
        zy_gdoc_drawing: {
            check: (editor: Editor) => isNodeInSchema('zyGdocDrawing', editor) && isSelectionTopNode(editor),
            action: ({ editor }: { editor: Editor }) => {
                editor.commands.insertZyGdocDrawing();
            },
        },
    };
};

function organizeItemsByGroups(items: SuggestionItem[], showGroups: boolean): SuggestionItem[] {
    if (!showGroups) {
        return items.map(item => ({ ...item, group: '' }));
    }

    const groups: { [groupLabel: string]: SuggestionItem[] } = {};

    // Group items
    items.forEach(item => {
        const groupLabel = item.group || '';
        if (!groups[groupLabel]) {
            groups[groupLabel] = [];
        }
        groups[groupLabel].push(item);
    });

    // Flatten groups in order (this maintains the visual order for keyboard navigation)
    const organizedItems: SuggestionItem[] = [];
    Object.entries(groups).forEach(([, groupItems]) => {
        organizedItems.push(...groupItems);
    });

    return organizedItems;
}

/**
 * Custom hook for slash dropdown menu functionality
 */
export function useSlashDropdownMenu(config?: SlashMenuConfig) {
    const getSlashMenuItems = useCallback(
        (editor: Editor) => {
            const items: SuggestionItem[] = [];

            const enabledItems = config?.enabledItems || (Object.keys(texts) as SlashMenuItemType[]);
            const showGroups = config?.showGroups !== false;

            const itemImplementations = getItemImplementations(config);

            enabledItems.forEach(itemType => {
                const itemImpl = itemImplementations[itemType];
                const itemText = texts[itemType];

                if (itemImpl && itemText && itemImpl.check(editor)) {
                    const insideZydefn = editor.isActive('zydefn') || editor.isActive('zydefnBlock');
                    const item: SuggestionItem = {
                        onSelect: ({ editor }) => itemImpl.action({ editor }),
                        ...itemText,
                        ...(itemType === 'zydefn' && insideZydefn ? { subtext: 'Remove zyDefinition' } : {}),
                    };

                    if (config?.itemGroups?.[itemType]) {
                        item.group = config.itemGroups[itemType];
                    } else if (!showGroups) {
                        item.group = '';
                    }

                    items.push(item);
                }
            });

            if (config?.customItems) {
                items.push(...config.customItems);
            }

            // Reorganize items by groups to ensure keyboard navigation works correctly
            return organizeItemsByGroups(items, showGroups);
        },
        [config],
    );

    return {
        getSlashMenuItems,
        config,
    };
}
